/**
 * fieldOrder — de veldwerkopdracht van projectleider naar veldwerker, in één vaste vorm.
 *
 * Elk adviesbureau heeft zijn eigen formulier (Excel met checkboxes, een Word-sjabloon, een
 * PDF). De AI zet zo'n formulier om naar dit schema; de projectleider kijkt het na en keurt het
 * goed; de veldwerker ziet het in de app als instructie. Omdat het schema vast is, kunnen we er
 * ook de eisen van BRL SIKB 2000 op loslaten (zie checkFieldOrder hieronder).
 *
 * Alles is optioneel (null of een lege lijst): een formulier dat iets niet noemt, heeft het niet
 * genoemd. De AI vult niets aan dat er niet staat -- wat ontbreekt, laat de controle zien.
 */

export type FieldProtocol = '2001' | '2002' | '2003' | '2018' | '2101';

/**
 * Onderzoeksstrategieën voor verkennend bodemonderzoek uit NEN 5740:2023+C1:2024 (§ 5, figuur 1),
 * met de vlak/tracé-varianten die de opdrachten gebruiken. Een veldwerkopdracht noemt er vaak
 * meer tegelijk: "VEP-OO, VEP en VED-HE-NL" bij een terrein met een tank, kernen en ophoging.
 */
export const NEN5740_CODES: { code: string; label: string }[] = [
  { code: 'ONV', label: 'onverdacht' },
  { code: 'ONV-NL', label: 'onverdacht, niet-lijnvormig' },
  { code: 'ONV-L', label: 'onverdacht, lijnvormig' },
  { code: 'ONV-GR', label: 'grootschalig onverdacht' },
  { code: 'VEP', label: 'verdacht, plaatselijke kern' },
  { code: 'VEP-OO', label: 'verdacht, ondergrondse opslagtank' },
  { code: 'VED-HO', label: 'verdacht, diffuus homogeen' },
  { code: 'VED-HO-NL', label: 'verdacht, diffuus homogeen, niet-lijnvormig' },
  { code: 'VED-HO-L', label: 'verdacht, diffuus homogeen, lijnvormig' },
  { code: 'VED-HE', label: 'verdacht, diffuus heterogeen' },
  { code: 'VED-HE-NL', label: 'verdacht, diffuus heterogeen, niet-lijnvormig' },
  { code: 'VED-HE-L', label: 'verdacht, diffuus heterogeen, lijnvormig' },
  { code: 'ONB', label: 'onbekende bodembelasting' },
  { code: 'NUL', label: 'nul- of eindonderzoek' },
];
const NEN5740_CODE_SET = new Set(NEN5740_CODES.map((c) => c.code));

/** Langste eerst, zodat 'VEP-OO' niet als 'VEP' wordt gelezen. Hoofdletters: 'nul' in een zin is geen code. */
const NEN5740_CODE_RE = /(?<![A-Z-])(VED-H[EO]-NL|VED-H[EO]-L|VED-H[EO]|VEP-OO|VEP|ONV-GR|ONV-NL|ONV-L|ONV|ONB|NUL)(?![A-Z-])/g;

/** Een code zoals een mens of het model hem schrijft ("ved he nl", "VEP–OO") → de vorm uit de norm, of null. */
export function normalizeNen5740Code(v: unknown): string | null {
  if (typeof v !== 'string') return null;
  const c = v.trim().toUpperCase().replace(/[\s_–—]+/g, '-').replace(/-+/g, '-');
  return NEN5740_CODE_SET.has(c) ? c : null;
}

/** De NEN 5740-codes die in een vrije tekst staan, in volgorde en zonder dubbele. */
export function nen5740CodesIn(text: string | null | undefined): string[] {
  return [...new Set([...(text || '').matchAll(NEN5740_CODE_RE)].map((m) => m[1]))];
}

export const FIELD_PROTOCOLS: { id: FieldProtocol; label: string }[] = [
  { id: '2001', label: 'Protocol 2001 — plaatsen van handboringen en peilbuizen' },
  { id: '2002', label: 'Protocol 2002 — nemen van grondwatermonsters' },
  { id: '2003', label: 'Protocol 2003 — veldwerk bij waterbodemonderzoek' },
  { id: '2018', label: 'Protocol 2018 — maaiveldinspectie en monsterneming asbest' },
  { id: '2101', label: 'Protocol 2101 — mechanisch boren' },
];

export interface FieldOrderPerson {
  name: string | null;
  company: string | null;
  phone: string | null;
  email: string | null;
}

export interface FieldOrderBorings {
  count: number | null;
  /** Einddiepte in meters onder maaiveld. */
  depthM: number | null;
  note: string | null;
}

export interface FieldOrderWells {
  count: number | null;
  depthM: number | null;
  /** Filterstelling in m-mv. */
  filterFromM: number | null;
  filterToM: number | null;
  /** freatisch, snijdend, ARVO, verticale afperking, … */
  type: string | null;
  note: string | null;
}

export interface FieldOrderPits {
  kind: 'gat' | 'sleuf' | null;
  count: number | null;
  depthM: number | null;
  /** Afmetingen zoals opgegeven, bv. "0,3 x 0,3 m" of "2,0 x 0,3 m". */
  dimensions: string | null;
  note: string | null;
}

export interface FieldOrder {
  project: {
    number: string | null;
    name: string | null;
    address: string | null;
    city: string | null;
    client: string | null;
  };
  projectLeader: FieldOrderPerson;
  /** Tweede projectleider of vervanger. */
  projectLeader2: FieldOrderPerson;
  siteContact: FieldOrderPerson;
  /** Hoe je op de locatie komt: sleutel, melden bij, tijdsafspraak. */
  access: string | null;
  /** Geplande datum(s) zoals opgegeven ("9 en 10 april 2026") en, als het kan, de eerste als ISO. */
  plannedDateText: string | null;
  plannedDate: string | null;
  /** Laatste dag als het veldwerk meer dagen duurt (ISO); null bij één dag. */
  plannedDateEnd: string | null;

  investigation: {
    /** verkennend, nader, asbest, waterbodem, indicatief, … */
    type: string | null;
    reason: string | null;
    goal: string | null;
    /** NEN 5740, NEN 5707, NEN 5720, NEN 5897, … met eventuele hypothese (VEP, ONV, …). */
    strategy: string | null;
    /** De NEN 5740-strategieën die de opdracht noemt (zie NEN5740_CODES), bv. ['VEP-OO', 'VEP', 'VED-HE-NL']. */
    strategyCodes: string[];
    suspicion: string | null;
    expectedContaminants: string[];
    expectedSoil: string | null;
    groundwaterFlow: string | null;
    /** Oppervlakte of lengte zoals opgegeven ("3,3 ha"). */
    area: string | null;
    pavements: string[];
  };

  protocols: FieldProtocol[];

  safety: {
    /** Veiligheidsklasse (CROW 400 / basishygiëne / T-klasse). */
    safetyClass: string | null;
    ppe: string[];
    /** null = niet genoemd; true/false = expliciet ja of nee. */
    klic: boolean | null;
    cablesAndPipes: string | null;
    trafficMeasures: string | null;
    /** Taakrisico's uit de TRA: asbest, werken langs de weg, langs het spoor, op water, … */
    risks: string[];
    otherMeasures: string | null;
  };

  work: {
    borings: FieldOrderBorings[];
    wells: FieldOrderWells[];
    pits: FieldOrderPits[];
    /** Asfalt/beton: kernboringen, wie, hoe dik. */
    hardSurface: string | null;
    equipment: string[];
    /** Machinale boring, ramguts, graafmachine met machinist, … */
    machinery: string | null;
  };

  sampling: {
    /** Instructies voor grond: mengmonsters, trajecten, asbestmonsters. */
    soil: string[];
    groundwater: string | null;
    asbestos: string | null;
  };

  surveying: {
    /** GPS, dGPS, x/y t.o.v. vast punt, … */
    method: string | null;
    heightNap: boolean | null;
    photos: boolean | null;
    note: string | null;
  };

  lab: {
    name: string | null;
    customerCode: string | null;
    packages: string[];
    packaging: string | null;
  };

  /** Inleveren: "digitaal uiterlijk volgende ochtend", "eigen veldwerkformulier binnen 24 uur". */
  delivery: string[];
  /** Overige opmerkingen en bijzonderheden die nergens anders passen. */
  remarks: string[];
  /** Bijlagen die het formulier noemt: boorplan, KLIC, kaart, offerte. */
  attachments: string[];
}

const person = (): FieldOrderPerson => ({ name: null, company: null, phone: null, email: null });

export function emptyFieldOrder(): FieldOrder {
  return {
    project: { number: null, name: null, address: null, city: null, client: null },
    projectLeader: person(),
    projectLeader2: person(),
    siteContact: person(),
    access: null,
    plannedDateText: null,
    plannedDate: null,
    plannedDateEnd: null,
    investigation: {
      type: null, reason: null, goal: null, strategy: null, strategyCodes: [], suspicion: null,
      expectedContaminants: [], expectedSoil: null, groundwaterFlow: null, area: null, pavements: [],
    },
    protocols: [],
    safety: { safetyClass: null, ppe: [], klic: null, cablesAndPipes: null, trafficMeasures: null, risks: [], otherMeasures: null },
    work: { borings: [], wells: [], pits: [], hardSurface: null, equipment: [], machinery: null },
    sampling: { soil: [], groundwater: null, asbestos: null },
    surveying: { method: null, heightNap: null, photos: null, note: null },
    lab: { name: null, customerCode: null, packages: [], packaging: null },
    delivery: [],
    remarks: [],
    attachments: [],
  };
}

const isoDate = (v: unknown) => (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v) && !Number.isNaN(Date.parse(v)) ? v : null);

/** Van en tot: een tot vóór of gelijk aan de van valt weg (dan is het één dag). */
function plannedDatesOf(src: any) {
  const plannedDate = isoDate(src.plannedDate);
  const end = isoDate(src.plannedDateEnd);
  return { plannedDate, plannedDateEnd: plannedDate && end && end > plannedDate ? end : null };
}

const MONTHS = ['januari', 'februari', 'maart', 'april', 'mei', 'juni', 'juli', 'augustus', 'september', 'oktober', 'november', 'december'];

/**
 * De datum van uitvoering om te tonen: "9 april 2026", "9 – 10 april 2026", "30 april – 2 mei 2026".
 * Zonder datums de tekst zoals opgegeven (oudere opdrachten, of "week 15").
 */
export function formatPlannedDates(o: Pick<FieldOrder, 'plannedDate' | 'plannedDateEnd' | 'plannedDateText'>): string | null {
  if (!o.plannedDate) return o.plannedDateText || null;
  const [y1, m1, d1] = o.plannedDate.split('-').map(Number);
  if (!o.plannedDateEnd) return `${d1} ${MONTHS[m1 - 1]} ${y1}`;
  const [y2, m2, d2] = o.plannedDateEnd.split('-').map(Number);
  if (y1 !== y2) return `${d1} ${MONTHS[m1 - 1]} ${y1} – ${d2} ${MONTHS[m2 - 1]} ${y2}`;
  if (m1 !== m2) return `${d1} ${MONTHS[m1 - 1]} – ${d2} ${MONTHS[m2 - 1]} ${y2}`;
  return `${d1} – ${d2} ${MONTHS[m2 - 1]} ${y2}`;
}

/**
 * Strategie en strategiecodes samen: de codes uit de lijst van het model, aangevuld met codes die
 * in de strategie- of verdachtheidstekst staan. "NEN4740" naast NEN 5740-codes is een tikfout
 * (komt zo voor in formulieren); die wordt NEN 5740.
 */
function strategyOf(inv: any, str: (v: unknown) => string | null) {
  const listed = (Array.isArray(inv?.strategyCodes) ? inv.strategyCodes : []).map(normalizeNen5740Code).filter(Boolean) as string[];
  const strategyCodes = [...new Set([...listed, ...nen5740CodesIn(str(inv?.strategy)), ...nen5740CodesIn(str(inv?.suspicion))])];
  let strategy = str(inv?.strategy);
  if (strategy && strategyCodes.length) strategy = strategy.replace(/\bNEN[\s-]?4740\b/i, 'NEN 5740');
  return { strategy, strategyCodes };
}

/**
 * Een (deels) ingevuld object terug naar de volledige vorm: ontbrekende takken aanvullen,
 * verkeerde typen wegpoetsen. Zo kan alles erna (controle, weergave) op de vorm vertrouwen,
 * ook als het uit de AI of uit een oudere versie van de database komt.
 */
export function normalizeFieldOrder(input: unknown): FieldOrder {
  const base = emptyFieldOrder() as any;
  const src = (input && typeof input === 'object' ? input : {}) as any;
  const str = (v: unknown) => (typeof v === 'string' && v.trim() ? v.trim() : typeof v === 'number' ? String(v) : null);
  const num = (v: unknown) => {
    if (typeof v === 'number' && Number.isFinite(v)) return v;
    if (typeof v === 'string') { const n = Number(v.replace(',', '.')); return Number.isFinite(n) ? n : null; }
    return null;
  };
  const bool = (v: unknown) => (typeof v === 'boolean' ? v : null);
  const strs = (v: unknown) => (Array.isArray(v) ? v.map(str).filter((s): s is string => !!s) : []);
  const pers = (v: any): FieldOrderPerson => ({ name: str(v?.name), company: str(v?.company), phone: str(v?.phone), email: str(v?.email) });

  const out: FieldOrder = {
    project: {
      number: str(src.project?.number), name: str(src.project?.name), address: str(src.project?.address),
      city: str(src.project?.city), client: str(src.project?.client),
    },
    projectLeader: pers(src.projectLeader),
    projectLeader2: pers(src.projectLeader2),
    siteContact: pers(src.siteContact),
    access: str(src.access),
    plannedDateText: str(src.plannedDateText),
    ...plannedDatesOf(src),
    investigation: {
      type: str(src.investigation?.type), reason: str(src.investigation?.reason), goal: str(src.investigation?.goal),
      ...strategyOf(src.investigation, str),
      suspicion: str(src.investigation?.suspicion),
      expectedContaminants: strs(src.investigation?.expectedContaminants), expectedSoil: str(src.investigation?.expectedSoil),
      groundwaterFlow: str(src.investigation?.groundwaterFlow), area: str(src.investigation?.area),
      pavements: strs(src.investigation?.pavements),
    },
    protocols: (Array.isArray(src.protocols) ? src.protocols : [])
      .map((p: unknown) => String(p).replace(/\D/g, ''))
      .filter((p: string): p is FieldProtocol => FIELD_PROTOCOLS.some((d) => d.id === p))
      .filter((p: string, i: number, a: string[]) => a.indexOf(p) === i),
    safety: {
      safetyClass: str(src.safety?.safetyClass), ppe: strs(src.safety?.ppe), klic: bool(src.safety?.klic),
      cablesAndPipes: str(src.safety?.cablesAndPipes), trafficMeasures: str(src.safety?.trafficMeasures),
      risks: strs(src.safety?.risks), otherMeasures: str(src.safety?.otherMeasures),
    },
    work: {
      borings: (Array.isArray(src.work?.borings) ? src.work.borings : []).map((b: any) => ({
        count: num(b?.count), depthM: num(b?.depthM), note: str(b?.note),
      })),
      wells: (Array.isArray(src.work?.wells) ? src.work.wells : []).map((w: any) => ({
        count: num(w?.count), depthM: num(w?.depthM), filterFromM: num(w?.filterFromM), filterToM: num(w?.filterToM),
        type: str(w?.type), note: str(w?.note),
      })),
      pits: (Array.isArray(src.work?.pits) ? src.work.pits : []).map((p: any) => ({
        kind: p?.kind === 'gat' || p?.kind === 'sleuf' ? p.kind : null, count: num(p?.count), depthM: num(p?.depthM),
        dimensions: str(p?.dimensions), note: str(p?.note),
      })),
      hardSurface: str(src.work?.hardSurface), equipment: strs(src.work?.equipment), machinery: str(src.work?.machinery),
    },
    sampling: { soil: strs(src.sampling?.soil), groundwater: str(src.sampling?.groundwater), asbestos: str(src.sampling?.asbestos) },
    surveying: {
      method: str(src.surveying?.method), heightNap: bool(src.surveying?.heightNap),
      photos: bool(src.surveying?.photos), note: str(src.surveying?.note),
    },
    lab: { name: str(src.lab?.name), customerCode: str(src.lab?.customerCode), packages: strs(src.lab?.packages), packaging: str(src.lab?.packaging) },
    delivery: strs(src.delivery),
    remarks: strs(src.remarks),
    attachments: strs(src.attachments),
  };
  return { ...base, ...out };
}

// ── Controle tegen BRL SIKB 2000 ────────────────────────────────────────────

export type FieldOrderCheckLevel = 'required' | 'advised';

export interface FieldOrderCheck {
  /** 'required' = de BRL vraagt het in het veldwerkplan; 'advised' = verstandig, geen eis. */
  level: FieldOrderCheckLevel;
  /** Pad in het schema, voor de backoffice om het veld te markeren (bv. 'safety.klic'). */
  field: string;
  /** Bij welk protocol de eis hoort, of null voor algemeen. */
  protocol: FieldProtocol | null;
  message: string;
}

const hasText = (s: string | null | undefined) => !!s && s.trim().length > 0;
const totalCount = (rows: { count: number | null }[]) => rows.reduce((n, r) => n + (r.count || 0), 0);

/**
 * Wat ontbreekt er in deze opdracht om volgens BRL SIKB 2000 te kunnen werken?
 *
 * Bewust een korte, uitlegbare lijst: elke regel is iets wat de veldwerker in het veld nodig
 * heeft of wat de BRL in het veldwerkplan vraagt (projectgegevens, protocol, strategie,
 * veiligheid, aantallen en dieptes, inmeten, laboratorium). Geen oordeel over de inhoud: of
 * NEN 5740 de juiste strategie is, beslist de projectleider.
 */
export function checkFieldOrder(o: FieldOrder): FieldOrderCheck[] {
  const out: FieldOrderCheck[] = [];
  const req = (field: string, protocol: FieldProtocol | null, message: string) => out.push({ level: 'required', field, protocol, message });
  const adv = (field: string, protocol: FieldProtocol | null, message: string) => out.push({ level: 'advised', field, protocol, message });
  const p = new Set(o.protocols);

  // Algemeen — wat elk veldwerkplan moet hebben.
  if (!hasText(o.project.number)) req('project.number', null, 'Projectnummer ontbreekt.');
  if (!hasText(o.project.address) && !hasText(o.project.city) && !hasText(o.project.name)) req('project.address', null, 'Locatie (adres of plaats) ontbreekt.');
  if (!hasText(o.project.client)) adv('project.client', null, 'Opdrachtgever niet genoemd.');
  if (!hasText(o.projectLeader.name)) req('projectLeader.name', null, 'Projectleider ontbreekt.');
  if (!hasText(o.projectLeader.phone)) req('projectLeader.phone', null, 'Telefoonnummer van de projectleider ontbreekt: de veldwerker moet hem kunnen bellen bij afwijkingen.');
  if (!p.size) req('protocols', null, 'Geen protocol gekozen (2001, 2002, 2003, 2018 of 2101).');
  if (!hasText(o.investigation.strategy) && !o.investigation.strategyCodes.length) req('investigation.strategy', null, 'Onderzoeksstrategie (bv. NEN 5740, NEN 5707) ontbreekt.');
  if (!hasText(o.investigation.goal) && !hasText(o.investigation.reason)) adv('investigation.goal', null, 'Aanleiding en doel ontbreken.');
  if (!hasText(o.plannedDateText) && !o.plannedDate) adv('plannedDate', null, 'Geen datum van uitvoering.');
  if (!hasText(o.access) && !hasText(o.siteContact.name)) adv('access', null, 'Geen toegang of contactpersoon op locatie.');
  if (!hasText(o.safety.safetyClass)) req('safety.safetyClass', null, 'Veiligheidsklasse ontbreekt (CROW 400 of basishygiëne).');
  if (!hasText(o.surveying.method)) req('surveying.method', null, 'Hoe de punten ingemeten worden (GPS, dGPS, t.o.v. vast punt) ontbreekt.');
  if (!hasText(o.lab.name)) req('lab.name', null, 'Laboratorium ontbreekt.');
  if (!o.lab.packages.length) req('lab.packages', null, 'Analysepakket ontbreekt.');

  const drilling = p.has('2001') || p.has('2101') || p.has('2018');
  if (drilling && o.safety.klic !== true) {
    req('safety.klic', null, o.safety.klic === false
      ? 'Er wordt geboord of gegraven zonder KLIC-melding.'
      : 'KLIC-melding niet genoemd; bij boren of graven is die verplicht.');
  }

  // Protocol 2001 — boringen en peilbuizen.
  if (p.has('2001')) {
    if (!o.work.borings.length && !o.work.wells.length) req('work.borings', '2001', 'Aantal boringen en einddiepte ontbreken.');
    o.work.borings.forEach((b, i) => {
      if (!b.count) req(`work.borings.${i}.count`, '2001', `Boringen regel ${i + 1}: aantal ontbreekt.`);
      if (!b.depthM) req(`work.borings.${i}.depthM`, '2001', `Boringen regel ${i + 1}: einddiepte ontbreekt.`);
    });
    o.work.wells.forEach((w, i) => {
      if (!w.count) req(`work.wells.${i}.count`, '2001', `Peilbuizen regel ${i + 1}: aantal ontbreekt.`);
      if (w.filterFromM == null || w.filterToM == null) adv(`work.wells.${i}.filter`, '2001', `Peilbuizen regel ${i + 1}: filterstelling niet opgegeven.`);
    });
  }

  // Protocol 2002 — grondwater.
  if (p.has('2002')) {
    if (!o.work.wells.length && !hasText(o.sampling.groundwater)) req('work.wells', '2002', 'Grondwatermonsters gevraagd, maar geen peilbuizen of bestaande peilbuizen genoemd.');
    if (!o.lab.packages.some((x) => /grondwater|water|nen\s*5740/i.test(x)) && !hasText(o.sampling.groundwater)) {
      adv('lab.packages', '2002', 'Geen analysepakket voor grondwater herkenbaar.');
    }
  }

  // Protocol 2003 — waterbodem.
  if (p.has('2003')) {
    if (!o.work.borings.length) req('work.borings', '2003', 'Aantal boringen (per vak) ontbreekt.');
    if (!o.safety.risks.some((r) => /water/i.test(r))) adv('safety.risks', '2003', 'Werken op of aan het water staat niet bij de risico\'s.');
  }

  // Protocol 2018 — asbest.
  if (p.has('2018')) {
    if (!o.work.pits.length && !o.work.borings.length) req('work.pits', '2018', 'Aantal inspectiegaten/-sleuven met afmetingen ontbreekt.');
    o.work.pits.forEach((g, i) => {
      if (!g.count) req(`work.pits.${i}.count`, '2018', `Gaten/sleuven regel ${i + 1}: aantal ontbreekt.`);
      if (!hasText(g.dimensions) && !g.depthM) req(`work.pits.${i}.dimensions`, '2018', `Gaten/sleuven regel ${i + 1}: afmetingen of diepte ontbreken.`);
    });
    if (!o.attachments.some((a) => /kaart|boorplan|tekening|situatie/i.test(a))) {
      req('attachments', '2018', 'Protocol 2018 eist een kaart met deelgebieden, inspectiestroken en de plekken van gaten en sleuven.');
    }
    if (!o.lab.packages.some((x) => /asbest|nen\s*58(97|98)/i.test(x))) adv('lab.packages', '2018', 'Geen asbestanalyse (NEN 5898/5897) in het analysepakket.');
  }

  // Protocol 2101 — mechanisch boren.
  if (p.has('2101') && !hasText(o.work.machinery)) adv('work.machinery', '2101', 'Mechanisch boren: welk boorsysteem of welke machine ontbreekt.');

  return out;
}

/** Korte samenvatting van het werk, voor in een lijst of kop ("21 × 1 m, 8 × 2 m, 9 peilbuizen"). */
export function summarizeFieldWork(o: FieldOrder): string {
  const nl = (n: number) => String(n).replace('.', ',');
  const parts: string[] = [];
  for (const b of o.work.borings) if (b.count) parts.push(`${b.count} × ${b.depthM ? `${nl(b.depthM)} m` : 'boring'}`);
  const pb = totalCount(o.work.wells);
  if (pb) parts.push(pb === 1 ? '1 peilbuis' : `${pb} peilbuizen`);
  for (const g of o.work.pits) {
    if (!g.count) continue;
    const word = g.kind === 'sleuf' ? (g.count === 1 ? 'sleuf' : 'sleuven') : (g.count === 1 ? 'gat' : 'gaten');
    parts.push(`${g.count} ${word}`);
  }
  return parts.join(', ');
}
